// Workspace = the single source of truth at runtime.
// - holds the materialised dataset in memory (fast synchronous reads for the UI)
// - every write is a domain command → Changeset → validated → committed transactionally
//   by the adapter → applied to memory → subscribers notified
// - every write appends an AuditEvent in the same transaction
// There is exactly one Workspace per open database. No module writes storage directly.
import {
  applyChangeset, DatasetView, DomainError, newId, today, validateRow, TABLE_NAMES, isLocalTable,
  type Changeset, type Ctx, type Dataset, type Put, type TableName, type ValidationIssue, type WorkspaceView,
} from '@vero/domain';
import { StorageError, type StorageAdapter } from './adapter.ts';
import { normalizeRow } from './codec.ts';

export type Command = (v: WorkspaceView, ctx: Ctx) => Changeset;

export class ValidationError extends Error {
  issues: ValidationIssue[];
  constructor(issues: ValidationIssue[]) { super(`Invalid data: ${issues.slice(0, 3).map((i) => `${i.table}.${i.field} ${i.problem}`).join('; ')}`); this.issues = issues; }
}

export interface WorkspaceOptions {
  actor?: string;
  region?: string;
  /** clock injection for tests */
  clock?: () => { now: string; today: string };
  audit?: boolean;
  /** device id; when set together with `outbox`, every local write also queues sync operations in the same transaction */
  deviceId?: string;
  outbox?: boolean;
}

/** Tables whose audit events keep field-level before/after values (spec §115). */
export const AUDIT_VALUE_TABLES: ReadonlySet<string> = new Set(['persons', 'policies', 'policy_adjustments', 'claims', 'documents', 'financial_items', 'consents', 'commission_entries', 'commission_rules', 'tax_rulesets', 'renewals', 'production_events']);
const IGNORED_FIELDS = new Set(['updatedAt', 'version', 'createdAt']);
/** Fields whose value differs between two versions of a row (JSON comparison). */
export function changedFields(prev: Record<string, unknown> | undefined, next: Record<string, unknown>): string[] {
  const keys = new Set([...Object.keys(prev ?? {}), ...Object.keys(next)]);
  const out: string[] = [];
  for (const k of keys) {
    if (IGNORED_FIELDS.has(k) || k === 'id') continue;
    if (JSON.stringify(prev?.[k] ?? null) !== JSON.stringify(next[k] ?? null)) out.push(k);
  }
  return out.sort();
}
const clip = (v: unknown) => { const s = typeof v === 'string' ? v : JSON.stringify(v ?? null); return s.length > 160 ? s.slice(0, 157) + '…' : v ?? null; };
/** restricted/medical documents: the audit records which fields changed, never their values */
const redacted = (table: string, row: Record<string, unknown> | undefined) => table === 'documents' && !!row && (row.restricted === true || row.category === 'medical');

export type WriteOrigin = 'local' | 'remote';

type Listener = (v: DatasetView, cs: Changeset | null) => void;

export class Workspace {
  readonly adapter: StorageAdapter;
  private _view: DatasetView;
  private listeners = new Set<Listener>();
  private queue: Promise<unknown> = Promise.resolve();
  private opts: WorkspaceOptions;
  private undoStack: { cs: Changeset; inverse: Changeset; at: string }[] = [];
  schemaVersion = 0;

  private constructor(adapter: StorageAdapter, ds: Dataset, opts: WorkspaceOptions) {
    this.adapter = adapter;
    this._view = new DatasetView(ds);
    this.opts = opts;
  }

  static async open(adapter: StorageAdapter, opts: WorkspaceOptions = {}): Promise<Workspace> {
    const { schemaVersion } = await adapter.open();
    const ds = await adapter.loadAll();
    const ws = new Workspace(adapter, ds, opts);
    ws.schemaVersion = schemaVersion;
    return ws;
  }

  get view(): DatasetView { return this._view; }
  get data(): Dataset { return this._view.data; }

  ctx(): Ctx {
    const c = this.opts.clock?.() ?? { now: new Date().toISOString(), today: today() };
    return { ...c, actor: this.opts.actor ?? 'local', region: this.opts.region };
  }
  setRegion(region: string) { this.opts.region = region; }

  subscribe(fn: Listener): () => void { this.listeners.add(fn); return () => this.listeners.delete(fn); }
  private emit(cs: Changeset | null) { for (const l of this.listeners) { try { l(this._view, cs); } catch (e) { console.error('[workspace listener]', e); } } }

  /** Serialise writes: commands run one at a time against the latest state. */
  run(cmd: Command, opts: { origin?: WriteOrigin } = {}): Promise<Changeset> {
    const job = this.queue.then(() => this.execute(cmd, opts.origin ?? 'local'));
    this.queue = job.catch(() => undefined);
    return job;
  }

  /** Start/stop queueing sync operations for local writes (the outbox lives in `sync_ops`, same transaction). */
  enableOutbox(deviceId: string) { this.opts.deviceId = deviceId; this.opts.outbox = true; }
  disableOutbox() { this.opts.outbox = false; }
  get outboxEnabled() { return !!this.opts.outbox && !!this.opts.deviceId; }
  get deviceId() { return this.opts.deviceId; }
  /** read-only mode (expired offline lease, locked device): writes are refused, reads keep working */
  readOnlyReason: string | null = null;
  /**
   * requirement 20: a narrower lock than read-only — return a reason to refuse ONE local change (e.g. a premium module's
   * records without the plan feature: `plan_feature:<feature>`). Reads, and every other change, keep working.
   */
  writeGuard: ((cs: Pick<Changeset, 'action'>) => string | null) | null = null;

  private async execute(cmd: Command, origin: WriteOrigin = 'local'): Promise<Changeset> {
    if (this.readOnlyReason && origin === 'local') throw new StorageError('workspace.read_only', this.readOnlyReason);
    const ctx = this.ctx();
    const raw = cmd(this._view, ctx);
    const refused = origin === 'local' ? this.writeGuard?.(raw) ?? null : null;
    if (refused) throw new StorageError('workspace.read_only', refused);
    const cs: Changeset = { ...raw, puts: raw.puts.map((p) => ({ table: p.table, row: normalizeRow(p.row) })) };
    const issues = cs.puts.flatMap((p) => validateRow(p.table, p.row as unknown as Record<string, unknown>));
    if (issues.length) throw new ValidationError(issues);
    const business = cs.puts.filter((p) => !isLocalTable(p.table) && p.table !== 'audit_events');
    const businessDeletes = cs.deletes.filter((d) => !isLocalTable(d.table));
    if (this.opts.audit !== false && business.length + businessDeletes.length > 0) {
      const changes: { t: string; id: string; f: Record<string, unknown> }[] = [];
      for (const p of business.slice(0, 50)) {
        const row = p.row as unknown as Record<string, unknown>;
        const prev = this._view.get(p.table, row.id as string) as unknown as Record<string, unknown> | undefined;
        const fields = changedFields(prev, row);
        if (!fields.length) continue;
        const withValues = AUDIT_VALUE_TABLES.has(p.table) && !redacted(p.table, row) && !redacted(p.table, prev);
        changes.push({ t: p.table, id: row.id as string, f: Object.fromEntries(fields.map((f) => [f, withValues ? [clip(prev?.[f]), clip(row[f])] : null])) });
      }
      cs.puts.push({ table: 'audit_events', row: normalizeRow({ id: newId(), createdAt: ctx.now, updatedAt: ctx.now, version: 1, at: ctx.now, actor: ctx.actor, action: cs.action, entity: cs.entity ?? null, entityId: cs.entityId ?? null, summary: cs.summary ?? null, data: { puts: business.length, deletes: businessDeletes.length, origin, changes } }) });
    }
    if (origin === 'local' && this.outboxEnabled) cs.puts.push(...this.outboxPuts(cs, ctx));
    const inverse = this.inverseOf(cs, ctx.now);
    await this.adapter.commit(cs); // throws → memory untouched
    this._view = new DatasetView(applyChangeset(this._view.data, cs));
    // v13: a secured change (policy deletion/restore) is never undone with Ctrl+Z — only through its own secured flow
    if (origin === 'local' && !cs.noUndo && !cs.action.startsWith('undo:') && inverse.puts.length + inverse.deletes.length > 0) {
      this.undoStack.push({ cs, inverse, at: ctx.now });
      if (this.undoStack.length > 30) this.undoStack.shift();
    }
    this.emit(cs);
    return cs;
  }

  /** One sync operation per replicated row change, written in the same transaction as the change itself. */
  private outboxPuts(cs: Changeset, ctx: Ctx): Put[] {
    const csId = newId();
    const out: Put[] = [];
    const deviceId = this.opts.deviceId!;
    for (const p of cs.puts) {
      if (isLocalTable(p.table)) continue;
      const row = p.row as unknown as Record<string, unknown>;
      const prev = this._view.get(p.table, row.id as string) as unknown as Record<string, unknown> | undefined;
      const forced = !!cs.syncBase && (row.id as string) in cs.syncBase;
      const fields = forced ? changedFields(undefined, row) : changedFields(prev, row);
      if (prev && !fields.length) continue;
      // v13: a policy deletion/restore carries its server step-up grant (the sync server refuses it otherwise)
      const grant = cs.grants?.[row.id as string] ?? null;
      out.push({ table: 'sync_ops', row: normalizeRow({ id: newId(), createdAt: ctx.now, updatedAt: ctx.now, version: 1, csId, table: p.table, rowId: row.id, kind: 'put', baseVersion: cs.syncBase?.[row.id as string] ?? Number(prev?.version ?? 0), rowVersion: Number(row.version ?? 1), fields, row, at: ctx.now, deviceId, state: 'pending', stepUpGrant: grant }) } as Put);
    }
    for (const d of cs.deletes) {
      if (isLocalTable(d.table)) continue;
      const prev = this._view.get(d.table, d.id) as unknown as Record<string, unknown> | undefined;
      if (!prev) continue;
      out.push({ table: 'sync_ops', row: normalizeRow({ id: newId(), createdAt: ctx.now, updatedAt: ctx.now, version: 1, csId, table: d.table, rowId: d.id, kind: 'delete', baseVersion: Number(prev.version ?? 0), rowVersion: Number(prev.version ?? 0) + 1, fields: [], at: ctx.now, deviceId, state: 'pending' }) } as Put);
    }
    return out;
  }

  /** Inverse changeset: restores every touched row to its prior content (with a new version). Audit rows are kept. */
  private inverseOf(cs: Changeset, now: string): Changeset {
    const puts: Changeset['puts'] = [];
    const deletes: Changeset['deletes'] = [];
    for (const p of cs.puts) {
      if (p.table === 'audit_events' || isLocalTable(p.table)) continue;
      const id = (p.row as { id: string }).id;
      const prev = this._view.get(p.table, id) as unknown as { version?: number } | undefined;
      const nextVersion = (p.row as { version?: number }).version ?? 1;
      if (prev) puts.push({ table: p.table, row: { ...(prev as object), version: nextVersion + 1, updatedAt: now } as never });
      else deletes.push({ table: p.table, id });
    }
    for (const d of cs.deletes) {
      const prev = this._view.get(d.table, d.id);
      if (prev) puts.push({ table: d.table, row: { ...(prev as object), updatedAt: now } as never });
    }
    return { action: `undo:${cs.action}`, entity: cs.entity, entityId: cs.entityId, summary: cs.summary, puts, deletes };
  }

  canUndo(): { action: string; summary?: string } | null {
    const top = this.undoStack[this.undoStack.length - 1];
    return top ? { action: top.cs.action, summary: top.cs.summary } : null;
  }

  /** Undo the most recent command, if none of its rows changed since (otherwise refuse, never clobber). */
  undo(): Promise<Changeset | null> {
    const job = this.queue.then(async () => {
      const top = this.undoStack[this.undoStack.length - 1];
      if (!top) return null;
      // content check (ignoring version/updatedAt): refuse if anything touched by the command changed since
      const strip = (r: unknown) => { if (!r) return 'null'; const { version: _v, updatedAt: _u, ...rest } = r as Record<string, unknown>; return JSON.stringify(rest, Object.keys(rest).sort()); };
      for (const p of top.cs.puts) {
        if (p.table === 'audit_events' || isLocalTable(p.table)) continue;
        const cur = this._view.get(p.table, (p.row as { id: string }).id);
        if (strip(cur) !== strip(p.row)) { this.undoStack.pop(); throw new StorageError('undo.conflict', 'Changed since — cannot undo'); }
      }
      this.undoStack.pop();
      const inv = top.inverse;
      return this.execute(() => ({ ...inv, puts: inv.puts.map((p) => ({ ...p, row: { ...(p.row as object), updatedAt: this.ctx().now } as never })) }), 'local');
    });
    this.queue = job.catch(() => undefined);
    return job;
  }

  /** Replace everything (restore / import-replace). Caller is responsible for snapshots & verification. */
  async replaceAll(ds: Dataset): Promise<void> {
    await this.queue;
    const clean = Object.fromEntries(TABLE_NAMES.map((t) => [t, (ds[t] ?? []).map((r) => normalizeRow(r))])) as unknown as Dataset;
    await this.adapter.replaceAll(clean);
    this.undoStack = []; // a restore/import is its own safety net (snapshot); older undo entries no longer apply
    this._view = new DatasetView(await this.adapter.loadAll());
    this.emit(null);
  }

  /** Re-read everything from storage (after external changes, or to verify). */
  async reload(): Promise<void> {
    this._view = new DatasetView(await this.adapter.loadAll());
    this.emit(null);
  }

  counts(): Record<TableName, number> {
    return Object.fromEntries(TABLE_NAMES.map((t) => [t, this.data[t].length])) as Record<TableName, number>;
  }
}

export function isDomainError(e: unknown): e is DomainError { return e instanceof DomainError; }
export function errorKey(e: unknown): string {
  if (e instanceof DomainError) return `errors.${e.code}`;
  if (e instanceof StorageError && e.code === 'workspace.read_only' && e.message.startsWith('licence_')) return 'errors.workspace.read_only_licence';
  if (e instanceof StorageError && e.code === 'workspace.read_only' && e.message.startsWith('plan_feature')) return 'errors.workspace.read_only_plan';
  if (e instanceof StorageError) return e.code === 'storage.quota' || e.code === 'workspace.read_only' || e.code.startsWith('crypto.') ? `errors.${e.code}` : 'errors.storage.failed';
  return 'common.error';
}
